import { ReactElement } from "react";
import styled from "styled-components";
import { safeIframeSrc } from "../safeUrl";

/**
 * A third-party player/widget the researcher embedded by URL (Acast, Spotify, Ausha, a
 * form, a map…).
 *
 * The author may paste the provider's whole `<iframe …>` snippet in Diapason, but only
 * the extracted `src` ever reaches us — this rebuilds a controlled iframe from it, so no
 * authored HTML is ever injected into the participant app. safeIframeSrc keeps it https-only.
 */
interface IframeData {
  src?: string;
  caption?: string;
  aspect?: "16/9" | "4/3" | "1/1" | "fixed";
  height?: number;
}

// Same permission list as the stock embed block (BasicBlocks) — players need autoplay
// and encrypted-media, fullscreen is for the video-shaped ones.
const ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";

const HEIGHT_MIN = 60;
const HEIGHT_MAX = 1200;
const DEFAULT_FIXED_HEIGHT = 188;

const Figure = styled.figure`
  margin: 0;
`;

/* Fixed height for players whose chrome does not scale (a 188px podcast bar letterboxed
   by a ratio clips its own controls on a phone); aspect-ratio for video-shaped embeds. */
const Frame = styled.iframe<{ $ratio?: string; $height?: number }>`
  width: 100%;
  display: block;
  border: none;
  border-radius: 16px;
  ${({ $ratio }) => ($ratio ? `aspect-ratio: ${$ratio};` : "")}
  ${({ $height }) => ($height ? `height: ${$height}px;` : "")}
`;

const Caption = styled.figcaption`
  font-size: 13px;
  text-align: center;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
  margin-top: 6px;
`;

const IframeBlock = ({ data }: { data: IframeData }): ReactElement | null => {
  const src = safeIframeSrc(data.src);
  if (!src) return null;

  const isFixed = data.aspect === "fixed";
  const height = Math.min(
    HEIGHT_MAX,
    Math.max(HEIGHT_MIN, Number(data.height) || DEFAULT_FIXED_HEIGHT)
  );

  return (
    <Figure>
      <Frame
        src={src}
        // An iframe with no accessible name is announced as "frame" and nothing else;
        // the caption is the authored, already language-resolved label for it.
        title={data.caption || "Embedded content"}
        $ratio={isFixed ? undefined : data.aspect ?? "16/9"}
        $height={isFixed ? height : undefined}
        loading="lazy"
        allow={ALLOW}
        allowFullScreen
      />
      {data.caption ? <Caption>{data.caption}</Caption> : null}
    </Figure>
  );
};

export default IframeBlock;
